import { NODE_DIMENSIONS, NODE_SIZE } from './node-defaults';
import { NODE_LAYOUT } from './node-layout';

/**
 * Square is the default shape, not an aspect-ratio lock.
 *
 * - `default`: new node, or a saved width whose height was never chosen.
 * - `auto`: content aspect (landscape / portrait) while the user has not resized.
 * - `manual`: a corner drag. Content updates must not replace it.
 */
export type NodeSizeSource = 'default' | 'auto' | 'manual';

export interface NodeBox {
  width: number;
  height: number;
  sizeSource: NodeSizeSource;
}

/** Floor so controls stay usable. Port rows can raise this. */
export const NODE_MIN_HEIGHT = 160;

export function nodeMinBox(portMin?: { width?: number; height?: number }) {
  return {
    width: Math.max(NODE_LAYOUT.minWidth, portMin?.width ?? 0),
    height: Math.max(NODE_MIN_HEIGHT, portMin?.height ?? 0),
  };
}

export function cssLength(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return undefined;
}

export function defaultNodeBox(componentType?: string): NodeBox {
  const dims = (componentType && NODE_DIMENSIONS[componentType]) || { width: NODE_SIZE, height: NODE_SIZE };
  return { width: dims.width, height: dims.height, sizeSource: 'default' };
}

function clampBox(
  width: number,
  height: number,
  min: { width: number; height: number },
): { width: number; height: number } {
  return {
    width: Math.max(min.width, Math.round(width)),
    height: Math.max(min.height, Math.round(height)),
  };
}

/**
 * Fit content into a box whose long side is `side`.
 * `aspect` is contentHeight / contentWidth.
 * Landscape content widens the box; portrait content tallies it; near-square stays square.
 */
export function fitContentAspect(
  side: number,
  aspect: number,
  min: { width: number; height: number } = nodeMinBox(),
): { width: number; height: number } {
  if (!Number.isFinite(aspect) || aspect <= 0 || !Number.isFinite(side) || side <= 0) {
    return clampBox(NODE_SIZE, NODE_SIZE, min);
  }
  if (Math.abs(aspect - 1) < 0.02) return clampBox(side, side, min);
  if (aspect < 1) return clampBox(side, side * aspect, min);
  return clampBox(side / aspect, side, min);
}

export interface ResolveNodeBoxInput {
  componentType?: string;
  savedWidth?: number;
  savedHeight?: number;
  sizeSource?: NodeSizeSource | string;
  /** contentHeight / contentWidth. Omit when content has no aspect. */
  contentAspect?: number;
  portMinWidth?: number;
  portMinHeight?: number;
}

/**
 * Decide the box for a node.
 * An explicit saved pair that is not the square default is kept (existing canvases).
 * A manual corner size is kept even when content aspect changes.
 * Otherwise content aspect may turn the square default into landscape or portrait.
 */
export function resolveNodeBox(input: ResolveNodeBoxInput): NodeBox {
  const min = nodeMinBox({ width: input.portMinWidth, height: input.portMinHeight });
  const fallback = defaultNodeBox(input.componentType);
  const savedW = cssLength(input.savedWidth);
  const savedH = cssLength(input.savedHeight);
  const source = input.sizeSource;

  if (source === 'manual' && savedW && savedH) {
    return { ...clampBox(savedW, savedH, min), sizeSource: 'manual' };
  }

  // A saved pair that is not the square catalog default was an explicit size.
  // Do not rewrite it to the new default and do not let content replace it.
  if (savedW && savedH && source !== 'auto' && source !== 'default') {
    const isSquareDefault = savedW === fallback.width && savedH === fallback.height;
    if (!isSquareDefault) {
      return { ...clampBox(savedW, savedH, min), sizeSource: 'manual' };
    }
  }

  if (cssLength(input.contentAspect) && source !== 'manual') {
    const side = savedW || fallback.width;
    return { ...fitContentAspect(side, input.contentAspect as number, min), sizeSource: 'auto' };
  }

  if (savedW && !savedH) {
    return { ...clampBox(savedW, savedW, min), sizeSource: 'default' };
  }

  if (savedW && savedH && source === 'default') {
    return { ...clampBox(savedW, savedH, min), sizeSource: 'default' };
  }

  return { ...clampBox(fallback.width, fallback.height, min), sizeSource: 'default' };
}

/**
 * Style for a newly created flow node. Non-square catalog leftovers are not
 * seeded — a missing or unequal height becomes the square default.
 * An already-square caller override (for example a 280² LoRA) is kept.
 */
export function initialFlowStyle(
  componentType?: string,
  dimensions?: { width?: number; height?: number },
): { width: number; height: number } {
  const fallback = defaultNodeBox(componentType);
  const width = cssLength(dimensions?.width);
  const height = cssLength(dimensions?.height);
  if (width && height && width === height) return { width, height };
  return { width: fallback.width, height: fallback.height };
}

/**
 * What the card should paint. A missing height is the square side, never
 * content-hug. While a corner drag is in progress, live dimensions lead.
 */
export function renderedNodeBox(input: {
  styleWidth?: number;
  styleHeight?: number;
  liveWidth?: number;
  liveHeight?: number;
  resizing?: boolean;
  fallbackWidth?: number;
}): { width: number; height: number } {
  if (input.resizing && cssLength(input.liveWidth) && cssLength(input.liveHeight)) {
    return { width: input.liveWidth as number, height: input.liveHeight as number };
  }
  const width = cssLength(input.styleWidth)
    ?? cssLength(input.liveWidth)
    ?? cssLength(input.fallbackWidth)
    ?? NODE_SIZE;
  const height = cssLength(input.styleHeight) ?? width;
  return { width, height };
}

/**
 * Corner drag reports flow coordinates. Zoom is already divided out by the
 * resizer — do not multiply by zoom or the stored size changes with the viewport.
 */
export function commitCornerResize(
  width: number,
  height: number,
  zoom: number,
  min: { width: number; height: number } = nodeMinBox(),
): NodeBox {
  void zoom;
  return { ...clampBox(width, height, min), sizeSource: 'manual' };
}
